import { uuid25 } from "../../../uuid/dist/uuid25.js";
import { query } from "../../../erd-editor-schema/dist/query/index.js";
import { ColumnOption } from "../../../constants/schema.js";
import { arrayHas } from "../../../utils/arrayHas.js";
import { addColumnAction, changeColumnAutoIncrementAction, changeColumnCommentAction, changeColumnDefaultAction, changeColumnLogicalNameAction, changeColumnNameAction, changeColumnNameDerivedAction, changeColumnNotNullAction, changeColumnPrimaryKeyAction, changeColumnUniqueAction, removeColumnAction } from "./atom.actions.js";
import { toDomainTypeActions } from "../dictionary/domainTypes.js";
import { bHas } from "../../../utils/bit.js";
import { FocusType, SelectType } from "../editor/state.js";
import { getFirstColumnType, getRemoveFirstColumnId } from "../editor/utils/focus.js";
import { focusColumnAction, focusTableAction } from "../editor/atom.actions.js";
import { removeIndexAction } from "../index/atom.actions.js";
import { removeRelationshipAction } from "../relationship/atom.actions.js";
import { last } from "es-toolkit";
//#region src/engine/modules/table-column/generator.actions.ts
var isToggleColumnTypes = arrayHas([
	FocusType.columnNotNull,
	FocusType.columnUnique,
	FocusType.columnAutoIncrement
]);
var isChangeColumnTypes = arrayHas([
	FocusType.columnName,
	FocusType.columnLogicalName,
	FocusType.columnDataType,
	FocusType.columnDefault,
	FocusType.columnComment
]);
var addColumnAction$ = (tableId) => function* (state) {
	const { editor: { selectedMap } } = state;
	const focusType = getFirstColumnType(state);
	if (tableId) {
		const id = uuid25();
		yield addColumnAction({
			id,
			tableId
		});
		yield focusColumnAction({
			tableId,
			columnId: id,
			focusType,
			$mod: false,
			shiftKey: false
		});
		return;
	}
	const addColumnActions = Object.entries(selectedMap).filter(([, type]) => type === SelectType.table).map(([tableId]) => ({
		tableId,
		id: uuid25()
	}));
	for (const payload of addColumnActions) yield addColumnAction(payload);
	const lastAction = last(addColumnActions);
	if (lastAction) yield focusColumnAction({
		tableId: lastAction.tableId,
		columnId: lastAction.id,
		focusType,
		$mod: false,
		shiftKey: false
	});
};
var removeColumnAction$ = (tableId, columnIds) => function* (state) {
	const { doc: { relationshipIds, indexIds }, editor: { focusTable }, collections } = state;
	if (focusTable?.columnId) {
		const columnId = getRemoveFirstColumnId(state, columnIds);
		if (columnId) yield focusColumnAction({
			tableId: focusTable.tableId,
			columnId,
			focusType: focusTable.focusType,
			$mod: false,
			shiftKey: false
		});
		else yield focusTableAction({
			tableId: focusTable.tableId,
			focusType: FocusType.tableName
		});
	}
	const hasColumnIds = arrayHas(columnIds);
	const relationships = query(collections).collection("relationshipEntities").selectByIds(relationshipIds).filter(({ start, end }) => start.tableId === tableId && start.columnIds.some(hasColumnIds) || end.tableId === tableId && end.columnIds.some(hasColumnIds));
	const indexes = query(collections).collection("indexEntities").selectByIds(indexIds).filter((index) => index.tableId === tableId && query(collections).collection("indexColumnEntities").selectByIds(index.indexColumnIds).map(({ columnId }) => columnId).some(hasColumnIds));
	for (const { id } of indexes) yield removeIndexAction({ id });
	for (const { id } of relationships) yield removeRelationshipAction({ id });
	for (const columnId of columnIds) yield removeColumnAction({
		id: columnId,
		tableId
	});
};
var toggleColumnValueAction$ = (focusType, tableId, columnId) => function* ({ collections }) {
	if (!isToggleColumnTypes(focusType)) return;
	const column = query(collections).collection("tableColumnEntities").selectById(columnId);
	if (!column) return;
	switch (focusType) {
		case FocusType.columnNotNull:
			yield changeColumnNotNullAction({
				id: columnId,
				tableId,
				value: !bHas(column.options, ColumnOption.notNull)
			});
			break;
		case FocusType.columnUnique:
			yield changeColumnUniqueAction({
				id: columnId,
				tableId,
				value: !bHas(column.options, ColumnOption.unique)
			});
			break;
		case FocusType.columnAutoIncrement: yield changeColumnAutoIncrementAction({
			id: columnId,
			tableId,
			value: !bHas(column.options, ColumnOption.autoIncrement)
		});
	}
};
var changeColumnDataTypeAction$ = (payload) => function* (state) {
	yield toDomainTypeActions(state, { dataTypes: [payload] });
};
var changeColumnValueAction$ = (focusType, tableId, columnId, value) => function* ({ collections }) {
	if (!isChangeColumnTypes(focusType)) return;
	const column = query(collections).collection("tableColumnEntities").selectById(columnId);
	if (!column) return;
	const payload = {
		id: columnId,
		tableId,
		value
	};
	switch (focusType) {
		case FocusType.columnName:
			yield [changeColumnNameAction(payload), ...bHas(column.options, ColumnOption.nameDerived) ? [changeColumnNameDerivedAction({
				...payload,
				value: false
			})] : []];
			break;
		case FocusType.columnLogicalName:
			yield changeColumnLogicalNameAction(payload);
			break;
		case FocusType.columnDataType:
			yield changeColumnDataTypeAction$(payload);
			break;
		case FocusType.columnDefault:
			yield changeColumnDefaultAction(payload);
			break;
		case FocusType.columnComment: yield changeColumnCommentAction(payload);
	}
};
var changeColumnsPrimaryKeyAction$ = (tableId, columnIds) => function* ({ collections }) {
	const table = query(collections).collection("tableEntities").selectById(tableId);
	if (!table) return;
	const columns = query(collections).collection("tableColumnEntities").selectByIds(columnIds.filter(arrayHas(table.columnIds)));
	const isPrimaryKey = ({ options }) => bHas(options, ColumnOption.primaryKey);
	const value = !columns.every(isPrimaryKey);
	yield columns.filter((column) => isPrimaryKey(column) !== value).map(({ id }) => changeColumnPrimaryKeyAction({
		tableId,
		id,
		value
	}));
};
var changeColumnPrimaryKeyAction$ = (tableId, columnId) => changeColumnsPrimaryKeyAction$(tableId, [columnId]);
var actions$ = {
	addColumnAction$,
	removeColumnAction$,
	toggleColumnValueAction$,
	changeColumnDataTypeAction$,
	changeColumnValueAction$,
	changeColumnPrimaryKeyAction$,
	changeColumnsPrimaryKeyAction$
};
//#endregion
export { actions$, addColumnAction$, changeColumnDataTypeAction$, changeColumnPrimaryKeyAction$, changeColumnValueAction$, changeColumnsPrimaryKeyAction$, isChangeColumnTypes, isToggleColumnTypes, removeColumnAction$, toggleColumnValueAction$ };
